import React, { useState } from 'react';
import { 
  Check, X, Download, Zap, Shield, CreditCard, 
  HelpCircle, Clock, Users, Mic, ShoppingBag
} from 'lucide-react';

const Pricing = () => {
  const [billingPeriod, setBillingPeriod] = useState('monthly');

  const plans = [
    {
      name: 'Free',
      price: '$0',
      priceYearly: '$0',
      description: 'Perfect for trying out Twe',
      tokens: '6k tokens/day',
      features: [
        { text: '6,000 tokens/day', included: true },
        { text: '2 minutes voice time daily', included: true },
        { text: 'All 34 automations', included: true },
        { text: 'Browser control', included: true },
        { text: 'App launching', included: true },
        { text: 'System controls', included: true },
        { text: 'Spotify integration', included: true },
        { text: '1 concurrent session', included: true },
        { text: 'Community support', included: true },
        { text: 'Custom voices', included: false },
        { text: 'Priority support', included: false },
        { text: 'Team features', included: false },
      ],
      cta: 'Get Started Free',
      popular: false
    },
    {
      name: 'Pro',
      price: '$19',
      priceYearly: '$15',
      period: '/month',
      description: 'For power users and professionals',
      tokens: '30k tokens/day',
      savings: 'Save $48/year',
      features: [
        { text: '30,000 tokens/day (5x Free)', included: true, highlight: true },
        { text: '10 minutes voice time daily', included: true, highlight: true },
        { text: 'All 34 automations', included: true },
        { text: 'Browser control', included: true },
        { text: 'App launching', included: true },
        { text: 'System controls', included: true },
        { text: 'Spotify integration', included: true },
        { text: '3 concurrent sessions', included: true },
        { text: 'Custom voice options', included: true, highlight: true },
        { text: 'Priority support', included: true },
        { text: '10% grace period on limits', included: true },
        { text: 'Team features', included: false },
      ],
      cta: 'Start Pro Plan',
      popular: true
    },
    {
      name: 'Enterprise',
      price: '$49',
      priceYearly: '$39',
      period: '/month',
      description: 'For teams and heavy users',
      tokens: '90k tokens/day',
      savings: 'Save $120/year',
      features: [
        { text: '90,000 tokens/day (15x Free)', included: true, highlight: true },
        { text: '30 minutes voice time daily', included: true, highlight: true },
        { text: 'All 34 automations', included: true },
        { text: 'Browser control', included: true },
        { text: 'App launching', included: true },
        { text: 'System controls', included: true },
        { text: 'Spotify integration', included: true },
        { text: '10 concurrent sessions', included: true, highlight: true },
        { text: 'Custom voice options', included: true },
        { text: 'Highest priority support', included: true },
        { text: 'Dedicated account manager', included: true },
        { text: 'Team sharing & analytics', included: true, highlight: true },
      ],
      cta: 'Start Enterprise Plan',
      popular: false
    }
  ];

  const comparisonFeatures = [
    { name: 'Daily Tokens', free: '6k', pro: '30k', enterprise: '90k', icon: Zap },
    { name: 'Voice Time', free: '2 min', pro: '10 min', enterprise: '30 min', icon: Mic },
    { name: 'Concurrent Sessions', free: '1', pro: '3', enterprise: '10', icon: Users },
    { name: 'Rate Limit', free: '60 req/hr', pro: '300 req/hr', enterprise: '1000 req/hr', icon: Clock },
    { name: 'Custom Voices', free: false, pro: true, enterprise: true },
    { name: 'Priority Support', free: false, pro: true, enterprise: true },
    { name: 'Grace Period', free: false, pro: '10%', enterprise: '20%' },
    { name: 'Team Features', free: false, pro: false, enterprise: true },
    { name: 'API Access', free: false, pro: false, enterprise: true },
    { name: 'Usage Analytics', free: false, pro: false, enterprise: true },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-zinc-950 to-black pt-24 pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12" data-testid="pricing-header">
          <div className="inline-flex items-center space-x-2 mb-4">
            <Zap className="w-6 h-6 text-orange-500" />
            <span className="text-orange-500 font-semibold uppercase tracking-wider text-sm">
              Simple, Transparent Pricing
            </span>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-4">
            Choose Your Plan
          </h1>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            Start free, upgrade when you need more. No hidden fees, no surprises.
          </p>
        </div>

        {/* Purchase Note */}
        <div className="max-w-md mx-auto mb-8">
          <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-4 flex items-center space-x-3">
            <ShoppingBag className="w-5 h-5 text-orange-500 flex-shrink-0" />
            <p className="text-orange-400 text-sm font-medium">
              Purchase Plans inside the App
            </p>
          </div>
        </div>

        {/* Billing Toggle */}
        <div className="flex items-center justify-center mb-12">
          <div className="bg-zinc-900 p-1 rounded-lg border border-white/10">
            <button
              onClick={() => setBillingPeriod('monthly')}
              data-testid="billing-monthly"
              className={`px-6 py-2 rounded-md font-medium transition-all duration-200 ${
                billingPeriod === 'monthly'
                  ? 'bg-orange-500 text-white'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingPeriod('yearly')}
              data-testid="billing-yearly"
              className={`px-6 py-2 rounded-md font-medium transition-all duration-200 ${
                billingPeriod === 'yearly'
                  ? 'bg-orange-500 text-white'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              Yearly
              <span className="ml-2 text-xs text-green-400">(Save 20%)</span>
            </button>
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto mb-16">
          {plans.map((plan, index) => (
            <div
              key={index}
              data-testid={`plan-${plan.name.toLowerCase()}`}
              className={`relative bg-zinc-900/50 backdrop-blur-sm rounded-2xl p-6 transition-all duration-300 ${
                plan.popular
                  ? 'border-2 border-orange-500 shadow-2xl shadow-orange-500/20 transform md:scale-105 z-10'
                  : 'border border-white/5 hover:border-orange-500/30'
              }`}
            >
              {/* Popular Badge */}
              {plan.popular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <div className="bg-gradient-to-r from-orange-600 to-red-600 text-white px-4 py-1 rounded-full text-sm font-semibold flex items-center space-x-1">
                    <Zap className="w-4 h-4" />
                    <span>Most Popular</span>
                  </div>
                </div>
              )}

              {/* Plan Header */}
              <div className="text-center mb-6 pt-2">
                <h2 className="text-2xl font-bold text-white mb-2">{plan.name}</h2>
                <p className="text-gray-400 text-sm mb-4">{plan.description}</p>
                <div className="flex items-baseline justify-center">
                  <span className="text-5xl font-bold text-white">
                    {billingPeriod === 'yearly' ? plan.priceYearly : plan.price}
                  </span>
                  {plan.period && (
                    <span className="text-gray-400 ml-2">{plan.period}</span>
                  )}
                </div>
                {billingPeriod === 'yearly' && plan.savings && (
                  <p className="text-green-400 text-sm mt-2">{plan.savings}</p>
                )}
                <div className="mt-3 inline-flex items-center space-x-1 px-3 py-1 bg-orange-500/20 rounded-full">
                  <Zap className="w-4 h-4 text-orange-500" />
                  <span className="text-orange-400 text-sm font-medium">{plan.tokens}</span>
                </div>
              </div>

              {/* Features */}
              <ul className="space-y-3 mb-6">
                {plan.features.map((feature, featureIndex) => (
                  <li key={featureIndex} className="flex items-start space-x-2">
                    {feature.included ? (
                      <Check className={`w-4 h-4 flex-shrink-0 mt-0.5 ${feature.highlight ? 'text-green-400' : 'text-orange-500'}`} />
                    ) : (
                      <X className="w-4 h-4 text-gray-600 flex-shrink-0 mt-0.5" />
                    )}
                    <span className={`text-sm ${feature.included ? (feature.highlight ? 'text-white font-medium' : 'text-gray-300') : 'text-gray-500 line-through'}`}>
                      {feature.text}
                    </span>
                  </li>
                ))}
              </ul>

              {/* CTA Button */}
              <button
                data-testid={`cta-${plan.name.toLowerCase()}`}
                className={`w-full py-3 rounded-lg font-semibold transition-all duration-300 ${
                  plan.popular
                    ? 'bg-gradient-to-r from-orange-600 to-red-600 text-white hover:shadow-xl hover:shadow-orange-500/50 hover:scale-105'
                    : 'bg-zinc-800 text-white hover:bg-zinc-700'
                }`}
              >
                {plan.cta}
              </button>
            </div>
          ))}
        </div>

        {/* Trust Signals */}
        <div className="max-w-4xl mx-auto mb-16">
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-zinc-900/50 border border-white/5 rounded-xl p-6 text-center">
              <Shield className="w-10 h-10 text-green-500 mx-auto mb-3" />
              <h3 className="text-white font-semibold mb-2">30-Day Money-Back</h3>
              <p className="text-gray-400 text-sm">Not satisfied? Full refund, no questions asked.</p>
            </div>
            <div className="bg-zinc-900/50 border border-white/5 rounded-xl p-6 text-center">
              <CreditCard className="w-10 h-10 text-blue-500 mx-auto mb-3" />
              <h3 className="text-white font-semibold mb-2">Secure Payments</h3>
              <p className="text-gray-400 text-sm">Powered by LemonSqueezy. We never see your card.</p>
            </div>
            <div className="bg-zinc-900/50 border border-white/5 rounded-xl p-6 text-center">
              <HelpCircle className="w-10 h-10 text-orange-500 mx-auto mb-3" />
              <h3 className="text-white font-semibold mb-2">Cancel Anytime</h3>
              <p className="text-gray-400 text-sm">No contracts, no lock-in. Downgrade in one click.</p>
            </div>
          </div>
        </div>

        {/* Comparison Table */}
        <div className="max-w-5xl mx-auto mb-16">
          <h2 className="text-3xl font-bold text-white mb-8 text-center">
            Plan Comparison
          </h2>
          <div className="bg-zinc-900/50 border border-white/5 rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="comparison-table">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left py-4 px-6 text-gray-400 font-semibold">Feature</th>
                    <th className="text-center py-4 px-6 text-white font-semibold">Free</th>
                    <th className="text-center py-4 px-6 text-orange-500 font-semibold">Pro</th>
                    <th className="text-center py-4 px-6 text-white font-semibold">Enterprise</th>
                  </tr>
                </thead>
                <tbody>
                  {comparisonFeatures.map((feature, index) => (
                    <tr key={index} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                      <td className="py-4 px-6 text-gray-300 flex items-center space-x-2">
                        {feature.icon && <feature.icon className="w-4 h-4 text-gray-500" />}
                        <span>{feature.name}</span>
                      </td>
                      <td className="py-4 px-6 text-center">
                        {typeof feature.free === 'boolean' ? (
                          feature.free ? <Check className="w-5 h-5 text-green-500 mx-auto" /> : <X className="w-5 h-5 text-gray-600 mx-auto" />
                        ) : (
                          <span className="text-gray-300">{feature.free}</span>
                        )}
                      </td>
                      <td className="py-4 px-6 text-center bg-orange-500/5">
                        {typeof feature.pro === 'boolean' ? (
                          feature.pro ? <Check className="w-5 h-5 text-green-500 mx-auto" /> : <X className="w-5 h-5 text-gray-600 mx-auto" />
                        ) : (
                          <span className="text-orange-400 font-medium">{feature.pro}</span>
                        )}
                      </td>
                      <td className="py-4 px-6 text-center">
                        {typeof feature.enterprise === 'boolean' ? (
                          feature.enterprise ? <Check className="w-5 h-5 text-green-500 mx-auto" /> : <X className="w-5 h-5 text-gray-600 mx-auto" />
                        ) : (
                          <span className="text-white font-medium">{feature.enterprise}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Honesty Note */}
        <div className="max-w-4xl mx-auto mb-16 bg-gradient-to-r from-orange-500/10 to-red-500/10 border border-orange-500/20 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-white mb-3 flex items-center">
            <Shield className="w-5 h-5 text-orange-500 mr-2" />
            Our Pricing Philosophy
          </h3>
          <p className="text-gray-300 mb-3">
            We believe in honest, sustainable pricing. Here's what you should know:
          </p>
          <ul className="space-y-2 text-gray-400 text-sm">
            <li className="flex items-start space-x-2">
              <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
              <span><strong className="text-white">Free is really free</strong> - No "trial" that expires. Use 6,000 tokens daily forever.</span>
            </li>
            <li className="flex items-start space-x-2">
              <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
              <span><strong className="text-white">No hidden upsells</strong> - All 34 automations work on Free. Paid plans just add more capacity.</span>
            </li>
            <li className="flex items-start space-x-2">
              <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
              <span><strong className="text-white">OpenAI costs money</strong> - Your subscription helps us cover AI processing costs so we can keep improving Twe.</span>
            </li>
          </ul>
        </div>

        {/* FAQ Section */}
        <div className="max-w-4xl mx-auto mb-16">
          <h2 className="text-3xl font-bold text-white mb-8 text-center">
            Frequently Asked Questions
          </h2>
          <div className="space-y-4">
            {[
              {
                q: "What happens if I exceed my daily token limit?",
                a: "Your commands will be queued until the next day when your tokens reset. Pro users get a 10% grace period, Enterprise users get 20%. You'll receive a notification when approaching your limit."
              },
              {
                q: "Can I switch plans anytime?",
                a: "Yes! Upgrade instantly, downgrade at end of billing cycle. Pro-rated charges for upgrades, unused time credited for downgrades."
              },
              {
                q: "What's a 'token' and how many do I need?",
                a: "Tokens measure AI processing. One voice conversation uses ~300 tokens. Free plan (6k tokens) = ~20 conversations/day. Most users find Free sufficient for personal use."
              },
              {
                q: "Do I need to provide my own OpenAI API key?",
                a: "No! All plans include built-in AI powered by OpenAI. Your subscription covers the AI processing costs."
              },
              {
                q: "What payment methods do you accept?",
                a: "Credit/debit cards (Visa, Mastercard, Amex), PayPal, and major crypto currencies through LemonSqueezy."
              },
              {
                q: "Is there a student or non-profit discount?",
                a: "Yes! Email us at contact@twe-assistant.com with verification for 50% off Pro plan."
              }
            ].map((item, index) => (
              <div
                key={index}
                className="bg-zinc-900/50 border border-white/5 rounded-xl p-6 hover:border-orange-500/30 transition-all duration-300"
              >
                <h3 className="text-lg font-semibold text-white mb-3">{item.q}</h3>
                <p className="text-gray-400 leading-relaxed">{item.a}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="text-center bg-gradient-to-br from-zinc-900 to-black border border-orange-500/30 rounded-2xl p-12">
          <h2 className="text-3xl font-bold text-white mb-4">
            Ready to automate your workflow?
          </h2>
          <p className="text-xl text-gray-400 mb-8">
            Join 10,000+ users who save hours every week with Twe.
          </p>
          <a
            href="https://downloads.twe-assistant.com/latest/twe-voice-assistant-setup.exe"
            download
            data-testid="download-cta"
            className="inline-flex items-center space-x-2 px-10 py-4 bg-gradient-to-r from-orange-600 to-red-600 text-white font-semibold rounded-lg hover:shadow-xl hover:shadow-orange-500/50 transition-all duration-300 hover:scale-105"
          >
            <Download className="w-5 h-5" />
            <span>Download Free Version</span>
          </a>
          <p className="text-gray-400 mt-6">
            No credit card required • Windows only (Mac coming Q3 2025)
          </p>
        </div>
      </div>
    </div>
  );
};

export default Pricing;
